import { createAction, createSlice } from '@reduxjs/toolkit';
import { IDataSyncModel, isIDataSyncModel } from 'Data/ViewModels/IDataSyncModel';
import isEmptyObject from 'Lib/Object/isEmptyObject';
import { nanoid } from 'nanoid';
import fromEntries from 'object.fromentries';
import { isNil } from 'ramda';
import * as oPath from '../../../Lib/Object/Path/Mutable';
import deepEquals from '../../../Lib/Object/deepEquals';
import { propertyRequiresSync } from '../../ReduxViewModels/DataSync';
import { applySyncPatch, getDefaultDataSyncState } from '../DataSync/sync';
import {
  AddArrayEntryAction,
  AddSnapshotAction,
  ApplySnapshotAction,
  CopyValueAction,
  ImportAction,
  PatchValuesAction,
  RemoveArrayEntryAction,
  RemoveSnapshotAction,
  SetSnapshotPreviewIdAction,
  SetTestedByCheckedByAction,
  SetValueAction,
  SyncAction,
  SyncConflictAction,
  SyncConflictResolveAction,
  SyncErrorAction,
  SyncErrorResolveAction,
  SyncResolveAction,
} from '../DataSync/types';
import {
  IWorksheetEditorState,
  InitAction,
  InitResolveAction,
  PatchStatusAction,
  UploadImageAttachmentAction,
} from './types';

const enableSnapshotPrototype = process.env.REACT_APP_ENABLE_SNAPSHOTS_PROTOTYPE === 'true';

const name = 'worksheetEditor';

const initialState: IWorksheetEditorState = {
  // Worksheet read-only state
  worksheetId: null,
  worksheetType: {},
  config: {},

  // Worksheet sync state & tracking
  ...getDefaultDataSyncState(),

  // Workheet Status
  status: {
    // different operation progress
    synchronising: false,
    importing: false,
    deleting: false,
    deleted: false,
    settingTestedByCheckedBy: false,

    // calculated
    saving: false, // combination of any worksheet-related saving activity
    ready: false, // worksheet etc has been loaded once.
    readonly: {
      locked: false,
      lockedMessage: '',
      uncheckAllowed: false,
    },
    dirty: false,
    uploadingImage: false,
  },

  // Worksheet snapshots - experimental feature, needs to be enabled with config
  snapshots: {},
  snapshotPreview: {},
};

const slice = createSlice({
  name,
  initialState,
  reducers: {
    init: (state, action: InitAction) => {
      const { payload: { worksheetId } = {} } = action;
      state.worksheetId = worksheetId;
      Object.entries(getDefaultDataSyncState()).forEach(([key, val]) => ((state as any)[key] = val));
      state.status.ready = false;
      state.status.synchronising = true;
      if (enableSnapshotPrototype) {
        const stored = localStorage.getItem(`${state.worksheetId}_snapshots`);
        let snapshots = stored == null ? {} : JSON.parse(stored) || {};
        state.snapshots = snapshots;
      }
    },
    unready: (state) => {
      state.status.ready = false;
    },
    initResolve: (state, action: InitResolveAction) => {
      const { payload: { worksheet, worksheetType, config } = {} } = action;
      state.worksheetType = worksheetType;
      state.config = config;
      Object.entries(getDefaultDataSyncState(worksheet)).forEach(([key, val]) => ((state as any)[key] = val));
      state.status.ready = true;
      state.status.synchronising = false;
    },
    initReject: (state) => {
      state.status.ready = true;
      state.status.synchronising = false;
    },

    sync: (state, action: SyncAction) => {
      if (state.syncStatus === 'waiting') {
        state.syncStatus = 'syncing';
      }

      // old flag to be removed (check for references, may need a derived value)
      state.status.synchronising = true;
    },
    syncResolve: (state, action: SyncResolveAction) => {
      state.syncStatus = 'waiting';
      state.status.synchronising = false;

      if (action?.payload?.syncPatch) {
        applySyncPatch(state, action.payload.syncPatch);
      }
      state.status.dirty = false;
    },
    syncConflict: (state, action: SyncConflictAction) => {
      const {
        payload: { syncPatch: { clientModelBefore, serverModelReceived, syncParams } = {} },
      } = action;

      state.syncStatus = 'unresolvedConflict';
      if (serverModelReceived) state.syncModel_synced = serverModelReceived;
      if (syncParams) state.syncParams_synced = syncParams;
      state.syncModel_conflictClientBefore = isIDataSyncModel(clientModelBefore)
        ? (clientModelBefore as IDataSyncModel)
        : null;
    },
    syncConflictResolve: (state, action: SyncConflictResolveAction) => {
      applySyncPatch(state, {
        serverModelReceived: state.syncModel_synced,
        syncParams: state.syncParams,
        choice: action.payload,
      });
    },

    syncError: (state, action: SyncErrorAction) => {
      const {
        payload: { syncPatch: { syncParams } = {} },
      } = action;

      state.syncStatus = 'syncError';
      if (syncParams) state.syncParams_synced = syncParams;
    },
    syncErrorResolve: (state, action: SyncErrorResolveAction) => {
      state.syncStatus = 'waiting';
      state.status.synchronising = false;
    },

    import: (state, action: ImportAction) => {
      state.status.importing = true;
    },
    importResolve: (state) => {
      state.status.importing = false;
    },
    importReject: (state) => {
      state.status.importing = false;
    },

    addArrayEntry: (state, action: AddArrayEntryAction) => {
      const { payload: { index, name, data = {} } = {} } = action;
      oPath.createPathIfNotExists(state, `syncModel.${name}`);
      let oldValue = oPath.get(state, `syncModel.${name}`);

      // We may have phantom records that are placeholders for records we want to exist. When adding a new record we
      // want those phantomt records to be created as well, otherwise it will create the first record only
      const gapsToFill = Math.max((index ?? 0) - (oldValue ?? []).length, 0);
      if (!oldValue) oPath.set(state, `syncModel.${name}`, [...Array(gapsToFill).fill({}), data], true);
      else oldValue.splice(index, 0, ...Array(gapsToFill).fill({}), data);
    },
    removeArrayEntry: (state, action: RemoveArrayEntryAction) => {
      const {
        payload: { index, name },
      } = action;
      oPath.createPathIfNotExists(state.syncModel, name);
      const array = oPath.get<Array<any>>(state.syncModel, name);
      const someEntryIsBeingCreated = Array.isArray(array) && array.some((v) => isEmptyObject(v));

      if (someEntryIsBeingCreated) {
        // Do nothing, can't delete while creating. The epics will warn the user. See epic comments on why we do this.
      } else if (isNil(array)) {
        oPath.set(state.syncModel, name, [], true);
      } else {
        array.splice(index, 1);
      }
    },
    setValue: (state, action: SetValueAction) => {
      const { payload: { name, value } = {} } = action;
      oPath.set(state, `syncModel.${name}`, value, true);
      if (name !== undefined) state.status.dirty = propertyRequiresSync(state, name);
    },
    copyValue: (state, action: CopyValueAction) => {
      const {
        payload: { source, destination },
      } = action;
      oPath.set(state, `syncModel.${destination}`, oPath.get(state, `syncModel.${source}`), true);
    },
    patchValues: (state, action: PatchValuesAction) => {
      const { payload: { name, keyValuePairs } = {} } = action;
      if (keyValuePairs) {
        oPath.setMultiple(
          state,
          `syncModel.${name}`,
          fromEntries(keyValuePairs.map((kvp: any) => [kvp.key, kvp.value])),
          true
        );
      }
    },
    patchStatus: (state, action: PatchStatusAction) => {
      const { payload: { status } = {} } = action;
      oPath.setMultiple(state, 'status', status);
    },

    setTestedByCheckedBy: (state, action: SetTestedByCheckedByAction) => {
      state.status.settingTestedByCheckedBy = true;
    },
    setTestedByCheckedByResolve: (state) => {
      state.status.settingTestedByCheckedBy = false;
    },
    setTestedByCheckedByReject: (state) => {
      state.status.settingTestedByCheckedBy = false;
    },

    // Track whether we are uploading an image to the current worksheet
    // At the moment we only support one upload at a time so we use a single flag
    uploadImageAttachment: (state, _: UploadImageAttachmentAction) => {
      state.status.uploadingImage = true;
      state.status.uploadImageStatus = 'uploading';
    },
    uploadImageAttachmentResolve: (state) => {
      state.status.uploadingImage = false;
      state.status.uploadImageStatus = 'resolved';
    },
    uploadImageAttachmentReject: (state) => {
      state.status.uploadingImage = false;
      state.status.uploadImageStatus = 'rejected';
    },

    // EXPERIMENTAL FEATURES
    addSnapshot: (state, action: AddSnapshotAction) => {
      const { payload: { snapshot, description } = {} } = action;
      if (enableSnapshotPrototype) {
        let id = nanoid();
        oPath.set(
          state,
          `snapshots.${id}`,
          {
            id,
            date: new Date().toJSON(),
            description,
          },
          true
        );

        localStorage.setItem(`${state.worksheetId}_snapshots`, JSON.stringify(state.snapshots));
        localStorage.setItem(
          `${state.worksheetId}_snapshot_${id}`,
          JSON.stringify(snapshot || oPath.get(state, 'syncModel'))
        );
      }
    },
    applySnapshot: (state, action: ApplySnapshotAction) => {
      const { payload: { id } = {} } = action;
      if (enableSnapshotPrototype) {
        const stored = localStorage.getItem(`${state.worksheetId}_snapshot_${id}`);
        let snapshot = stored == null ? {} : JSON.parse(stored) || {};
        oPath.set(state, `syncModel`, snapshot, true);
        state.status.dirty = !deepEquals(state.syncModel, snapshot);
      }
    },
    removeSnapshot: (state, action: RemoveSnapshotAction) => {
      const { payload: { id } = {} } = action;
      if (enableSnapshotPrototype) {
        oPath.del(state, `snapshots.${id}`);
        localStorage.setItem(`${state.worksheetId}_snapshots`, JSON.stringify(state.snapshots));
        localStorage.removeItem(`${state.worksheetId}_snapshot_${id}`);
      }
    },
    setSnapshotPreviewId: (state, action: SetSnapshotPreviewIdAction) => {
      const { payload: { id } = {} } = action;
      if (enableSnapshotPrototype) {
        oPath.set(state, `snapshotPreview.id`, id, true);

        const stored = localStorage.getItem(`${state.worksheetId}_snapshot_${id}`);
        let snapshot = stored == null ? {} : JSON.parse(stored) || {};
        oPath.set(state, `snapshotPreview.snapshot`, snapshot, true);
      }
    },

    deleteWorksheet: (state) => {
      state.status.deleted = false;
      state.status.deleting = true;
    },
    deleteWorksheetResolve: (state) => {
      state.status.deleted = true;
      state.status.deleting = false;

      // Clear worksheet from state and un-ready editor
      state.worksheetId = null;
      Object.entries(getDefaultDataSyncState()).forEach(([key, val]) => ((state as any)[key] = val));
      state.status.ready = false;
    },
    deleteWorksheetReject: (state) => {
      state.status.deleted = false;
      state.status.deleting = false;
    },
    clearDeleteWorksheet: (state) => {
      state.status.deleted = false;
      state.status.deleting = false;
    },
  },
});

const { actions: sliceActions, reducer } = slice;
const actions = {
  ...sliceActions,
  persistTempState: createAction(`${name}/persistTempState`),
  syncConflictEarlyExit: createAction(`${name}/syncConflictEarlyExit`),
};
export { actions, reducer };
